import { useEffect, useRef, useState } from "react";
import { useUnit } from "effector-react";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { authenticationStore } from "@/state/auth/AuthStore";
import { Picture } from "@/type/core/offices/offices.types";
import { requestGetPictureFromUrl } from "@/state/picture/PictureEffects";
import { toastError } from "@utils/helpers/toast-helper";
import { blobToBase64, convertImageToBlob } from "@utils/helpers/base64-helper";
import { getToken } from "@utils/rest/RestClient";
import configuration from "@utils/Config";
import { useTranslation } from "react-i18next";
import ecmaLogo from "@/static/core/img/logo_ecma.png";

interface UseOfficesSignaturePicturesDefaultReturn {
  officeSignaturePictures: Picture[];
  officeSealingPictures: Picture[];
}

const useOfficesSignaturePicturesDefault = (
  officeId: string,
): UseOfficesSignaturePicturesDefaultReturn => {
  const { t } = useTranslation();
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const office = authStore?.user?.offices.find(
    (office) => office.id === officeId,
  );

  const [officeSignaturePictures, setOfficeSignaturePictures] = useState<
    Picture[]
  >([]);
  const [officeSealingPictures, setOfficeSealingPictures] = useState<Picture[]>(
    [],
  );
  const hasSetDefaultSignaturePictureRef = useRef(false);

  useEffect(() => {
    if (office) {
      setOfficeSignaturePictures([]);
      office.signaturePictureDefault?.forEach((picture) => {
        if (picture.picture) {
          const url = `${configuration.backendBaseUrl}${
            picture.picture
          }&token=${getToken()}`;
          requestGetPictureFromUrl(url)
            .then((result) => {
              if (!result.ok || !result.data) {
                // 404 here just mean that the user has no avatar picture because he can't find picture/{id} where id is undefined (just lead to /picture)
                toastError(`${t("error.toast.signaturePicture")} ${office.id}`);
                return;
              }
              createPictureFromBlob(result.data, (base64Data) => {
                setOfficeSignaturePictures((prevState) => {
                  const newPicture: Picture = {
                    ...picture,
                    picture: base64Data,
                  };
                  return [...prevState, newPicture];
                });
              });
            })
            .catch(() => {
              toastError(`${t("error.toast.signaturePicture")} ${office.id}`);
            });
        }
      });

      if (
        !office.signaturePictureDefault?.length &&
        !hasSetDefaultSignaturePictureRef.current
      ) {
        hasSetDefaultSignaturePictureRef.current = true;
        convertImageToBlob(ecmaLogo)
          .then((blob) => {
            blobToBase64(blob)
              .then((base64Data) => {
                if (base64Data) {
                  setOfficeSignaturePictures([
                    {
                      picture: base64Data,
                      rank: 1,
                      isDefault: true,
                      isDefaultImage: true,
                    },
                  ]);
                }
              })
              .catch(() => {
                toastError(t("offices.preferences.errors.blobToBase64"));
              });
          })
          .catch(() => {
            toastError(t("offices.preferences.errors.imgToBlob"));
          });
      }
    }
  }, [office]);

  useEffect(() => {
    if (office) {
      setOfficeSealingPictures([]);
      office.scellementPicture?.forEach((picture) => {
        if (picture.picture) {
          const url = `${configuration.backendBaseUrl}${
            picture.picture
          }&token=${getToken()}`;
          requestGetPictureFromUrl(url)
            .then((result) => {
              if (!result.ok || !result.data) {
                // 404 here just mean that the user has no avatar picture because he can't find picture/{id} where id is undefined (just lead to /picture)
                toastError(`${t("error.toast.sealingPicture")} ${office.id}`);
                return;
              }
              createPictureFromBlob(result.data, (base64Data) => {
                setOfficeSealingPictures((prevState) => {
                  const newPicture: Picture = {
                    ...picture,
                    picture: base64Data,
                  };
                  return [...prevState, newPicture];
                });
              });
            })
            .catch(() =>
              toastError(`${t("error.toast.sealingPicture")} ${office.id}`),
            );
        }
      });
    }
  }, [office]);

  const createPictureFromBlob = (
    blob: Blob,
    completion: (base64Data: string) => void,
  ) => {
    blobToBase64(blob)
      .then((base64Data) => {
        completion(base64Data);
      })
      .catch(() => {
        toastError(t("error.toast.pictureConversion"));
      });
  };

  return {
    officeSignaturePictures,
    officeSealingPictures,
  };
};

export default useOfficesSignaturePicturesDefault;
